import type { Breakpoint } from '@mui/material/styles';

import { Logo } from '@/components/logo';
import { RouterLink } from '@/routes/components';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

// ----------------------------------------------------------------------

const LINKS = [
  {
    headline: 'Legal',
    children: [
      { name: 'Terms and condition', href: '#' },
      { name: 'Privacy policy', href: '#' },
    ],
  },
  {
    headline: 'Contact',
    children: [{ name: 'one@robonone.ch', href: 'mailto:one@robonone.ch' }],
  },
];

// ----------------------------------------------------------------------

const FooterRoot = styled('footer')(({ theme }) => ({
  position: 'relative',
  backgroundColor: theme.vars.palette.background.default,
}));

export type FooterProps = React.ComponentProps<typeof FooterRoot>;

export function Footer({
  sx,
  layoutQuery = 'md',
  ...other
}: FooterProps & { layoutQuery?: Breakpoint }) {
  return (
    <FooterRoot sx={sx} {...other}>
      <Divider />

      <Container
        sx={(theme) => ({
          pb: 5,
          pt: 5,
          textAlign: 'center',
          [theme.breakpoints.up(layoutQuery)]: { textAlign: 'unset' },
        })}>
        <Grid
          container
          sx={[
            (theme) => ({
              mt: 3,
              justifyContent: 'center',
              [theme.breakpoints.up(layoutQuery)]: { justifyContent: 'space-between' },
            }),
          ]}>
          <Grid size={{ xs: 12, [layoutQuery]: 3 }}>
            <Box
              key="logo"
              sx={(theme) => ({
                width: 1,
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'column',
                [theme.breakpoints.up(layoutQuery)]: { alignItems: 'flex-start' },
              })}>
              <Logo isSingle={false} isMageba href="https://mageba-group.com" />
            </Box>
          </Grid>

          <Grid size={{ xs: 12, [layoutQuery]: 6 }}>
            <Box
              sx={(theme) => ({
                gap: 5,
                display: 'flex',
                flexDirection: 'column',
                [theme.breakpoints.up(layoutQuery)]: { flexDirection: 'row' },
              })}>
              <Box
                key="social media"
                sx={(theme) => ({
                  gap: 2,
                  width: 1,
                  display: 'flex',
                  alignItems: 'center',
                  flexDirection: 'column',
                  [theme.breakpoints.up(layoutQuery)]: { alignItems: 'flex-start' },
                })}>
                <Typography component="div" variant="overline">
                  social media
                </Typography>

                <Box
                  component="img"
                  sizes="(max-width: 70px)"
                  src="/assets/icons/linkedIn_logo.png"
                />
                <Box
                  component="img"
                  sx={{ objectFit: 'cover' }}
                  src="/assets/icons/youtube_logo.png"
                />
              </Box>

              {LINKS.map((list) => (
                <Box
                  key={list.headline}
                  sx={(theme) => ({
                    gap: 2,
                    width: 1,
                    display: 'flex',
                    alignItems: 'center',
                    flexDirection: 'column',
                    [theme.breakpoints.up(layoutQuery)]: { alignItems: 'flex-start' },
                  })}>
                  <Typography component="div" variant="overline">
                    {list.headline}
                  </Typography>

                  {list.children.map((link) => (
                    <Link
                      key={link.name}
                      component={RouterLink}
                      href={link.href}
                      color="inherit"
                      variant="body2">
                      {link.name}
                    </Link>
                  ))}
                </Box>
              ))}
            </Box>
          </Grid>
        </Grid>
      </Container>
      <Box sx={{ overflowX: 'hidden' }}>
        <Container
          maxWidth={false}
          disableGutters
          sx={(theme) => ({
            backgroundColor: theme.vars.palette.primary.main,
            height: '43px',
            textAlign: 'center',
            py: 0,
            display: 'flex',
            alignItems: 'center',
            width: '100%', // Changed from 100vw to 100%
            [theme.breakpoints.up(layoutQuery)]: { textAlign: 'unset' },
          })}>
          <Container
            sx={(theme) => ({
              display: 'flex',
              alignItems: 'center',
              [theme.breakpoints.up(layoutQuery)]: { textAlign: 'unset' },
            })}>
            <Typography variant="body1" sx={{ color: 'common.white' }}>
              mageba sa
            </Typography>
            <HeaderCenterArea />
            <Typography variant="body2" sx={{ color: 'common.white' }}>
              © 2025 All rights reserved.
            </Typography>
          </Container>
        </Container>
      </Box>
    </FooterRoot>
  );
}

const HeaderCenterArea = styled('div')(() => ({
  display: 'flex',
  flex: '1 1 auto',
  justifyContent: 'center',
}));
